import React, { memo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import styled from "styled-components";

import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import { Button } from "../../../mui/core";
import { extractTextFromPage } from "./utils";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

export const PDFPage = styled(Page)`
  border: 8px solid #525659;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.14), 0 3px 4px 0 rgba(0, 0, 0, 0.12),
    0 1px 0px 0 rgba(0, 0, 0, 0.18);
`;

export const PDFDocument = styled(Document)`
  display: flex;

  ${({ numPages, file }) =>
    numPages && file ? "" : "justify-content: center;"}
  align-items: center;
  flex-direction: column;
  flex: 1;
  border-left: solid 1px rgba(0, 0, 0, 0.12);

  & .react-pdf__message--no-data {
    height: 100%;
    width: 100%;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: auto;
  flex: 1;
`;
const Footer = styled.div`
  height: 66px;
  border-top: solid 1px rgba(0, 0, 0, 0.12);
`;
const PdfWrapper = styled.div`
  height: 100%;
  width: 100%%;
  overflow: auto;
  display: flex;
  flex-direction: column;
  row-gap: 8px;
`;
const PdfContent = styled.div`
  height: 100%;
  width: 100%;
  overflow: auto;
  display: flex;
`;
const PdfTools = styled.div`
  width: 100%;
  display: flex;
  justify-content: end;
  align-items: center;
  padding: 4px;
  column-gap: 8px;
`;

const PdfToolButton = styled(Button)`
  padding: 8px;
`;

export const PDFController = memo(
  ({ pdfControllerRef, formikPropsRef, fileData, noData }) => {
    const [numPages, setNumPages] = useState(null);
    const isPageAlreadyLoadedRef = useRef([]);
    const [zoom, setZoom] = useState(1);

    const handleZoom = increase =>
      setZoom(zoom => Math.min(Math.max(zoom + increase, 1), 3));

    const onDocumentLoadSuccess = ({ numPages }) => {
      isPageAlreadyLoadedRef.current = [];
      setNumPages(numPages);
    };

    const file = fileData?.data
      ? { data: new Uint8Array(new Uint8Array(fileData.data)) }
      : null;

    return (
      <Wrapper>
        <PdfWrapper>
          {numPages > 0 && (
            <PdfTools>
              <PdfToolButton
                variant="outlined"
                size="small"
                onClick={() => handleZoom(-0.5)}
              >
                -
              </PdfToolButton>
              <PdfToolButton
                variant="outlined"
                size="small"
                onClick={() => handleZoom(0.5)}
              >
                +
              </PdfToolButton>
            </PdfTools>
          )}
          <PdfContent>
            <PDFDocument
              inputRef={pdfControllerRef}
              file={file}
              numPages={numPages}
              onLoadSuccess={onDocumentLoadSuccess}
              options={{
                cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
                cMapPacked: true,
              }}
              noData={noData}
              loading={"Loading PDF file"}
              error={
                <React.Fragment>
                  Failed to load PDF file {noData}
                </React.Fragment>
              }
            >
              {[...Array(numPages)].map((_, i) => (
                <PDFPage
                  key={i}
                  pageNumber={i + 1}
                  onLoadSuccess={page =>
                    parsePage(page, formikPropsRef, isPageAlreadyLoadedRef)
                  }
                  scale={zoom}
                />
              ))}
            </PDFDocument>
          </PdfContent>
        </PdfWrapper>
        <Footer />
      </Wrapper>
    );
  }
);

export async function parsePage(page, formikPropsRef, isPageAlreadyLoadedRef) {
  if (isPageAlreadyLoadedRef.current.includes(page._pageIndex)) return;

  await extractTextFromPage(page, formikPropsRef);

  const operatorList = await page.getOperatorList();
  const resolvedImages = [];

  new Set(
    [...operatorList.argsArray]
      .filter(
        arg =>
          arg &&
          Array.isArray(arg) &&
          typeof arg[0] === "string" &&
          arg[0].startsWith("img")
      )
      .map(([key]) => key)
  ).forEach(key => {
    const obj = page.objs.get(key);
    if (obj?.bitmap instanceof ImageBitmap) {
      resolvedImages.push(obj);
    }
  });

  const viewport = page.getViewport({ scale: 1 });
  const canvas = document.createElement("canvas");
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext("2d");

  await page.render({
    canvasContext: ctx,
    viewport: viewport,
  }).promise;

  const imgs = await Promise.all(
    resolvedImages.map(async imgObj => {
      try {
        const bmp = imgObj.bitmap;
        const imgCanvas = document.createElement("canvas");
        imgCanvas.width = bmp.width;
        imgCanvas.height = bmp.height;
        const imgCtx = imgCanvas.getContext("bitmaprenderer");
        imgCtx.transferFromImageBitmap(bmp);

        const blob = await new Promise(res => imgCanvas.toBlob(res));
        return URL.createObjectURL(blob);
      } catch (err) {
        return null;
      }
    })
  );

  const validImgs = imgs.filter(Boolean);

  const specDetails = validImgs.map(img => ({
    title: "Preview",
    type: "Preview",
    description: null,
    preview: img,
  }));

  formikPropsRef.current.setFieldValue("specDetails", [
    ...formikPropsRef.current.values.specDetails,
    ...specDetails,
  ]);

  isPageAlreadyLoadedRef.current.push(page._pageIndex);
}
